const PHONE_STORAGE_KEY = 'ng-erp-phone-preferences'

function loadPreferences () {
  try {
    return JSON.parse(localStorage.getItem(PHONE_STORAGE_KEY) || '{}')
  } catch (e) {
    return {}
  }
}

const persisted = loadPreferences()

export default {
  namespaced: true,
  state: {
    bootstrapLoading: false,
    bootstrapLoaded: false,
    bootstrapError: '',
    registrationState: 'idle',
    registrationError: '',
    disconnectedReason: '',
    unsupportedError: '',
    permissionError: '',
    reconnectAttempt: 0,
    extensions: [],
    selectedExtensionId: persisted.selectedExtensionId || null,
    sessionConfig: null,
    dialerVisible: persisted.dialerVisible === true,
    lastDialedNumber: '',
    numberInput: '',
    activeCall: null,
    incomingCall: null,
    incomingLeadLookup: {
      loading: false,
      error: '',
      found: null,
      phone: ''
    },
    isMuted: false,
    isOnHold: false,
    speakerEnabled: persisted.speakerEnabled !== false,
    selectedInputDeviceId: persisted.selectedInputDeviceId || '',
    selectedOutputDeviceId: persisted.selectedOutputDeviceId || '',
    availableInputDevices: [],
    availableOutputDevices: [],
    callLogs: [],
    callLogsLoading: false,
    callLogsError: '',
    callLogsPagination: {
      current_page: 1,
      per_page: 15,
      total: 0,
      last_page: 1
    }
  },
  getters: {
    isRegistered: state => state.registrationState === 'registered',
    hasPhoneExtension: state => state.bootstrapLoaded && state.extensions.length > 0,
    selectedExtension: state => state.extensions.find(item => item.id === state.selectedExtensionId) || null,
    hasActiveCall: state => !!state.activeCall,
    currentStatusText: state => {
      if (state.incomingCall) return 'Incoming call'
      if (state.activeCall) return state.activeCall.statusLabel || state.activeCall.status || 'Calling'
      if (state.registrationState === 'registered') return 'Ready'
      if (state.registrationState === 'registering') return 'Registering'
      if (state.registrationState === 'error') return state.registrationError || 'Registration failed'
      return 'Disconnected'
    }
  },
  mutations: {
    SET_BOOTSTRAP_LOADING (state, value) {
      state.bootstrapLoading = value
    },
    SET_BOOTSTRAP_LOADED (state, value) {
      state.bootstrapLoaded = value
    },
    SET_BOOTSTRAP_ERROR (state, value) {
      state.bootstrapError = value || ''
    },
    SET_REGISTRATION_STATE (state, value) {
      state.registrationState = value
    },
    SET_REGISTRATION_ERROR (state, value) {
      state.registrationError = value || ''
    },
    SET_DISCONNECTED_REASON (state, value) {
      state.disconnectedReason = value || ''
    },
    SET_UNSUPPORTED_ERROR (state, value) {
      state.unsupportedError = value || ''
    },
    SET_PERMISSION_ERROR (state, value) {
      state.permissionError = value || ''
    },
    SET_RECONNECT_ATTEMPT (state, value) {
      state.reconnectAttempt = value || 0
    },
    SET_EXTENSIONS (state, value) {
      state.extensions = value || []
      if (!state.selectedExtensionId && state.extensions.length) {
        const preferred = state.extensions.find(item => item.is_default) || state.extensions[0]
        state.selectedExtensionId = preferred.id
      }
    },
    SET_SELECTED_EXTENSION_ID (state, value) {
      state.selectedExtensionId = value
    },
    SET_SESSION_CONFIG (state, value) {
      state.sessionConfig = value
    },
    SET_DIALER_VISIBLE (state, value) {
      state.dialerVisible = typeof value === 'boolean' ? value : !state.dialerVisible
    },
    SET_LAST_DIALED_NUMBER (state, value) {
      state.lastDialedNumber = value || ''
    },
    SET_NUMBER_INPUT (state, value) {
      state.numberInput = value || ''
    },
    SET_ACTIVE_CALL (state, value) {
      state.activeCall = value
    },
    UPDATE_ACTIVE_CALL (state, value) {
      if (!state.activeCall) return
      state.activeCall = {
        ...state.activeCall,
        ...value
      }
    },
    SET_INCOMING_CALL (state, value) {
      state.incomingCall = value
    },
    SET_INCOMING_LEAD_LOOKUP (state, value) {
      state.incomingLeadLookup = {
        ...state.incomingLeadLookup,
        ...(value || {})
      }
    },
    RESET_INCOMING_LEAD_LOOKUP (state) {
      state.incomingLeadLookup = {
        loading: false,
        error: '',
        found: null,
        phone: ''
      }
    },
    SET_IS_MUTED (state, value) {
      state.isMuted = !!value
    },
    SET_IS_ON_HOLD (state, value) {
      state.isOnHold = !!value
    },
    SET_SPEAKER_ENABLED (state, value) {
      state.speakerEnabled = !!value
    },
    SET_SELECTED_INPUT_DEVICE_ID (state, value) {
      state.selectedInputDeviceId = value || ''
    },
    SET_SELECTED_OUTPUT_DEVICE_ID (state, value) {
      state.selectedOutputDeviceId = value || ''
    },
    SET_AVAILABLE_INPUT_DEVICES (state, value) {
      state.availableInputDevices = value || []
    },
    SET_AVAILABLE_OUTPUT_DEVICES (state, value) {
      state.availableOutputDevices = value || []
    },
    SET_CALL_LOGS_LOADING (state, value) {
      state.callLogsLoading = !!value
    },
    SET_CALL_LOGS_ERROR (state, value) {
      state.callLogsError = value || ''
    },
    SET_CALL_LOGS (state, value) {
      state.callLogs = value || []
    },
    SET_CALL_LOGS_PAGINATION (state, value) {
      state.callLogsPagination = {
        ...state.callLogsPagination,
        ...(value || {})
      }
    },
    UPSERT_CALL_LOG (state, callLog) {
      const index = state.callLogs.findIndex(item => item.id === callLog.id)
      if (index === -1) {
        state.callLogs = [callLog, ...state.callLogs]
        return
      }
      state.callLogs.splice(index, 1, {
        ...state.callLogs[index],
        ...callLog
      })
    },
    RESET_PHONE_STATE (state) {
      state.registrationState = 'idle'
      state.registrationError = ''
      state.disconnectedReason = ''
      state.permissionError = ''
      state.reconnectAttempt = 0
      state.sessionConfig = null
      state.activeCall = null
      state.incomingCall = null
      state.incomingLeadLookup = {
        loading: false,
        error: '',
        found: null,
        phone: ''
      }
      state.isMuted = false
      state.isOnHold = false
    }
  },
  actions: {
    persistPreferences ({ state }) {
      localStorage.setItem(PHONE_STORAGE_KEY, JSON.stringify({
        selectedExtensionId: state.selectedExtensionId,
        dialerVisible: state.dialerVisible,
        selectedInputDeviceId: state.selectedInputDeviceId,
        selectedOutputDeviceId: state.selectedOutputDeviceId,
        speakerEnabled: state.speakerEnabled
      }))
    }
  }
}
